/**
 * Live calling (LiveKit) server functions.
 *
 * - startOrJoinCall: verifies the caller is a conversation participant, creates
 *   (or reuses) an active call_session, mints a LiveKit join token and — for the
 *   initiator — posts a chat message and notifies the other party.
 * - endCall: marks the session ended, records the duration and posts a log
 *   message into the thread so the call appears in history.
 */
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { mintLiveKitToken, notifyIncomingCall } from "@/lib/livecall.server";
import { safeOrigin } from "@/lib/safe-origin";
import { recordUsage } from "@/lib/usage/metering.server";

export const startOrJoinCall = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) =>
    z
      .object({
        conversationId: z.string().uuid(),
        kind: z.enum(["video", "voice"]),
        callSessionId: z.string().uuid().optional(),
        origin: z.string().url().optional(),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const url = process.env.LIVEKIT_URL;
    const apiKey = process.env.LIVEKIT_API_KEY;
    const apiSecret = process.env.LIVEKIT_API_SECRET;
    if (!url || !apiKey || !apiSecret) {
      throw new Error("Live calling is not configured yet.");
    }

    // RLS-scoped read confirms the caller participates in this conversation.
    const { data: convo } = await context.supabase
      .from("conversation")
      .select("id, client_id, admin_id")
      .eq("id", data.conversationId)
      .maybeSingle();
    if (!convo) throw new Response("Forbidden", { status: 403 });
    if (convo.client_id !== context.userId && convo.admin_id !== context.userId) {
      throw new Response("Forbidden", { status: 403 });
    }

    let adminId = convo.admin_id;
    if (!adminId) {
      const { data: defaultAdminId } = await (context.supabase.rpc as any)("get_default_admin_id");
      adminId = (defaultAdminId as string | null) ?? null;
      if (adminId) {
        await context.supabase
          .from("conversation")
          .update({ admin_id: adminId })
          .eq("id", data.conversationId);
      }
    }

    const { data: me } = await context.supabase
      .from("profiles")
      .select("name")
      .eq("id", context.userId)
      .maybeSingle();
    const myName = me?.name?.trim() || "Someone";

    const room = `conv-${data.conversationId}`;
    let callSessionId = data.callSessionId ?? null;
    let isInitiator = false;

    if (!callSessionId) {
      const { data: session, error } = await context.supabase
        .from("call_session")
        .insert({
          conversation_id: data.conversationId,
          client_id: convo.client_id,
          admin_id: adminId,
          kind: data.kind,
          status: "active",
          provider: "livekit",
          provider_room: room,
          title: data.kind === "video" ? "Video call" : "Voice call",
          started_at: new Date().toISOString(),
          created_by: context.userId,
        })
        .select("id")
        .single();
      if (error) throw new Error(error.message);
      callSessionId = session.id;
      isInitiator = true;

      await context.supabase.from("message").insert({
        conversation_id: data.conversationId,
        sender_id: context.userId,
        kind: "text",
        body: `📞 ${myName} started a ${data.kind} call`,
      });

      const otherId =
        convo.client_id === context.userId ? adminId : convo.client_id;
      await notifyIncomingCall(otherId, myName, data.kind, safeOrigin(data.origin));
    }

    const token = await mintLiveKitToken({
      identity: context.userId,
      name: myName,
      room,
      apiKey,
      apiSecret,
    });

    return { token, url, room, callSessionId, isInitiator };
  });

export const endCall = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((data) =>
    z
      .object({
        callSessionId: z.string().uuid(),
        durationSeconds: z.number().int().min(0).max(86_400),
        answered: z.boolean().optional(),
      })
      .parse(data),
  )
  .handler(async ({ data, context }) => {
    const { data: session } = await context.supabase
      .from("call_session")
      .select("id, conversation_id, kind, status, duration_seconds, client_id")
      .eq("id", data.callSessionId)
      .maybeSingle();
    if (!session) return { ok: false };
    if (session.status === "ended" || session.status === "missed") return { ok: true };

    // A call that was never answered (hung up while still ringing) is logged
    // as "missed" rather than a 0-second call.
    const wasAnswered = data.answered !== false && data.durationSeconds > 0;

    await context.supabase
      .from("call_session")
      .update({
        status: wasAnswered ? "ended" : "missed",
        ended_at: new Date().toISOString(),
        duration_seconds: data.durationSeconds,
      })
      .eq("id", data.callSessionId);

    // Meter call minutes at the point of consumption (fail-open). The early
    // status guard above ensures this runs only once per call (the second
    // participant to hang up returns before reaching here).
    if (wasAnswered) {
      await recordUsage({
        eventType: "call",
        accountId: session.client_id,
        quantity: data.durationSeconds / 60,
        unit: "minutes",
      });
    }


    const kindLabel = session.kind === "video" ? "Video" : "Voice";
    let body: string;
    if (wasAnswered) {
      const mins = Math.floor(data.durationSeconds / 60);
      const secs = data.durationSeconds % 60;
      const dur = mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
      body = `📞 ${kindLabel} call ended · ${dur}`;
    } else {
      body = `📞 ${kindLabel} call · No answer`;
    }
    await context.supabase.from("message").insert({
      conversation_id: session.conversation_id,
      sender_id: context.userId,
      kind: "text",
      body,
    });

    return { ok: true };
  });
